import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, DollarSign, AlertCircle, Clock, Zap } from "lucide-react";

const computeMetrics = (pipelineData) => {
  const totalValue = pipelineData.reduce((sum, opp) => sum + opp.value, 0);
  const weightedValue = pipelineData.reduce((sum, opp) => sum + (opp.value * (opp.probability / 100)), 0);
  const dealsAtRisk = pipelineData.filter(opp => {
    const daysSinceUpdate = Math.floor((new Date() - new Date(opp.lastUpdated)) / (1000 * 60 * 60 * 24));
    return daysSinceUpdate > 14 && opp.stage !== "closed-won";
  }).length;
  return { totalValue, weightedValue, dealsAtRisk, dealCount: pipelineData.length };
};

const PipelineView = () => {
  const { userId } = useParams();
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);

  const stages = [
    { id: "qualified", name: "Qualified", color: "bg-ism-teal/80", borderColor: "border-ism-teal/30" },
    { id: "discovery", name: "Discovery", color: "bg-gray-500/80", borderColor: "border-gray-500/30" },
    { id: "proposal", name: "Proposal", color: "bg-amber-500/80", borderColor: "border-amber-500/30" },
    { id: "negotiation", name: "Negotiation", color: "bg-orange-500/80", borderColor: "border-orange-500/30" },
    { id: "closed-won", name: "Closed Won", color: "bg-emerald-500/80", borderColor: "border-emerald-500/30" }
  ];

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pipeline/${userId}`);
      setPipeline(response.data);
      setMetrics(computeMetrics(response.data));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast.error("Failed to load pipeline");
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const getOpportunitiesByStage = (stageId) => {
    return pipeline.filter(opp => opp.stage === stageId);
  };

  const handleCardClick = (opportunity) => {
    toast.info(`Opening deal: $${opportunity.value.toLocaleString()} - ${opportunity.probability}% probability`);
  };

  const getDaysInStage = (lastUpdated) => {
    if (!lastUpdated) return 0;
    const date = new Date(lastUpdated);
    if (isNaN(date.getTime())) return 0;
    return Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-ism-teal/50"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6" 
      data-testid="pipeline-view"
    >
      <div>
        <h1 className="text-2xl text-white mb-2 flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-ism-teal" />
          <span className="text-ism-teal">Pipeline</span>
        </h1>
        <p className="text-sm text-gray-500">Track and manage your sales opportunities</p>
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card-light rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 tracking-wider">Total Pipeline</p>
              <DollarSign className="h-4 w-4 text-ism-teal/50" />
            </div>
            <p className="text-xl text-white">${(metrics.totalValue / 1000).toFixed(0)}K</p>
          </Card>
          <Card className="glass-card-light rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 tracking-wider">Weighted Value</p>
              <TrendingUp className="h-4 w-4 text-ism-teal/50" />
            </div>
            <p className="text-xl text-white">${(metrics.weightedValue / 1000).toFixed(0)}K</p>
          </Card>
          <Card className="glass-card-light rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 tracking-wider">Deals at Risk</p>
              <AlertCircle className="h-4 w-4 text-red-400/70" />
            </div>
            <p className="text-xl text-red-400/80">{metrics.dealsAtRisk}</p>
          </Card>
          <Card className="glass-card-light rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 tracking-wider">Total Deals</p>
              <Clock className="h-4 w-4 text-ism-teal/50" />
            </div>
            <p className="text-xl text-white">{metrics.dealCount}</p>
          </Card>
        </div>
      )}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const opportunities = getOpportunitiesByStage(stage.id);
          const stageValue = opportunities.reduce((sum, opp) => sum + opp.value, 0);

          return (
            <div key={stage.id} className="flex-shrink-0 w-72" data-testid={`stage-${stage.id}`}>
              <Card className="glass-card rounded-xl h-full overflow-hidden">
                <div className={`${stage.color} p-4`}>
                  <h3 className="text-white text-sm">{stage.name}</h3>
                  <p className="text-white/70 text-xs">${(stageValue / 1000).toFixed(0)}K ({opportunities.length})</p>
                </div>
                <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
                  {opportunities.map((opp, idx) => (
                    <motion.div
                      key={opp.opportunityId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => handleCardClick(opp)}
                      className={`glass-card-light rounded-lg p-3 cursor-pointer hover:bg-white/10 transition-all border ${stage.borderColor}`}
                      data-testid={`deal-card-${idx}`}
                    >
                      <p className="text-white text-sm mb-2">Deal #{idx + 1}</p>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-ism-teal/80">${(opp.value / 1000).toFixed(0)}K</p>
                        <Badge className="bg-amber-500/10 text-amber-400/80 border-amber-500/30 text-xs">
                          {opp.probability}%
                        </Badge>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {getDaysInStage(opp.lastUpdated)} days in stage
                      </div>
                    </motion.div>
                  ))}
                  {opportunities.length === 0 && (
                    <p className="text-gray-600 text-xs text-center py-6">No deals in this stage</p>
                  )}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      {/* AI Recommendations */}
      <Card className="glass-card rounded-xl p-6">
        <h3 className="text-sm text-gray-400 mb-4 flex items-center gap-2">
          <Zap className="h-4 w-4 text-ism-teal/50" />
          AI Recommendations
        </h3>
        <div className="space-y-3">
          <div className="bg-ism-teal/5 border border-ism-teal/20 rounded-lg p-4">
            <p className="text-sm text-gray-400 leading-relaxed">
              <span className="text-ism-teal/80">High Priority:</span> You have {metrics?.dealsAtRisk || 0} deals at risk that haven&apos;t been updated in over 14 days. 
              Focus on moving these forward to prevent pipeline stagnation.
            </p>
          </div>
          <div className="bg-ism-teal/5 border border-ism-teal/20 rounded-lg p-4">
            <p className="text-sm text-gray-400 leading-relaxed">
              <span className="text-ism-teal/80">Strategy:</span> Your weighted pipeline is ${((metrics?.weightedValue || 0) / 1000).toFixed(0)}K. 
              To hit your goal, focus on accelerating high-probability deals in the Negotiation stage.
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default PipelineView;
